/**
 * @file machine_app_library.js
 * @author MachineApps Team
 *
 * @description The MachineAppLibrary contains MachineApp data
 * that is created from the definitions found in machine_app_definition.js.
 *
 */

const { v4: uuidv4 } = require('uuid');
const machine_app_implementation_loader = require('./machine_app_implementation_loader');
const { MachineAppApplication } = require('./machine_app_library/machine_app_application');
const { MachineAppConfiguration } = require('./machine_app_library/machine_app_configuration');
const { MachineParameters } = require('./machine_app_library/machine_app_parameters');
const { MachineAppGlobalAsset } = require('./machine_app_library/machine_app_global_asset');
const { MACHINEAPPS_SAVE_DISABLED_FLAG } = require('../constants');

function _uniqueName(pName, pNameList) {
  let _lName = pName;
  let _lCounter = 0;

  while(pNameList.includes(_lName)) {
    _lCounter += 1;
    _lName = pName + ' (' + _lCounter + ')';
  }
  return _lName;
}

/**
 * On result function
 * @typedef {(success: boolean) => void} onResultCallback
 */

/**
 * @param {(json: object, onResult: onResultCallback) => void} saveLibrary
 * @param {(machineAppType: string, itemType: string, json: object, onResult: onResultCallback) => void} upsertMachineAppItem
 * @param {(machineAppType: string, itemType: string, json: object, onResult: onResultCallback) => void} removeMachineAppItem
 */
function SaveFunctionality(saveLibrary, upsertMachineAppItem, removeMachineAppItem) {
  this.saveLibrary  = saveLibrary;
  this.upsert       = upsertMachineAppItem;
  this.remove       = removeMachineAppItem;
}

function getPassThroughSaveFunctionality() {
  return new SaveFunctionality(
    (json, onSuccess) => onSuccess(true),
    (machineAppType, itemType, json, onResult) => onResult(true),
    (machineAppType, itemType, json, onResult) => onResult(true)
  );
}

/**
 * Manages a list of libraries corresponding to the type of MachineApps that we support. Also contains methods
 * for modifying this type list.
 *
 * @param {Function<Object, Object, Function> => void} pSaveFunction Called whenever we modify the Library.
 */
class MachineAppLibraryManager {
  constructor() {
    this.mVersion           = 1;
    this.mLibraryList       = [];
    this.mSaveFunctionality = undefined;
    this.mLastLibraryJson   = undefined;
    this.mIsLoaded          = false;
    this.mMachineLogicLibrary = undefined;

    // NOTE: This is modified for path-following.vention.io to always be true,
    // as we don't want the "others" changing it. This causes the library to default
    // to a non-saved version, but admins (or those who know) can always alter that.
    localStorage.setItem(MACHINEAPPS_SAVE_DISABLED_FLAG, "true");
  }

  getSaveFunctionality() {
    if (localStorage.getItem(MACHINEAPPS_SAVE_DISABLED_FLAG) === 'true') {
      return getPassThroughSaveFunctionality();
    }

    return this.mSaveFunctionality;
  }
}

MachineAppLibraryManager.prototype.isLoaded = function() { return this.mIsLoaded;  };

/**
* @param {Function<Object, Object, Function> => void} pSaveFunction Called whenever we modify the Library.
*/
MachineAppLibraryManager.prototype.setSaveFunctionality = function(pSaveFunctionality) {
  this.mSaveFunctionality = new SaveFunctionality(pSaveFunctionality.saveAll, pSaveFunctionality.upsert, pSaveFunctionality.remove);
};

MachineAppLibraryManager.prototype.setMachineLogicLibrary = function(pMachineLogicLibrary) {
  this.mMachineLogicLibrary = pMachineLogicLibrary;
};

/**
 * @returns {(import('../../machinelogic').Library)}
 */
MachineAppLibraryManager.prototype.getMachineLogicLibrary = function() { return this.mMachineLogicLibrary; };

MachineAppLibraryManager.prototype.fromJson = function(pLibraryJson, pIsUndo) {
  if (!pLibraryJson) {
    console.error('Attempting to inflate from json with none defined.');
    return;
  }

  this.mVersion = pLibraryJson.version;
  this.mLibraryList = [];

  const lThis = this;
  pLibraryJson.libraryList.forEach(function(pTypeJson) {
    const lType             = pTypeJson.type;

    if (!machine_app_implementation_loader.instance.hasMachineAppType(lType)) {
      console.error(`Failed to load MachineAppLibrary json because the type is not supported: ${JSON.stringify(pTypeJson, null, 2)}`);
      return;
    }

    const lConfigList       = pTypeJson.configurationList;
    const lCalibrationList  = pTypeJson.calibrationList;
    const lApplicationList  = pTypeJson.applicationList;
    const lGlobalAssetList  = pTypeJson.globalAssetList;

    lThis.mLibraryList.push((new MachineAppLibrary(lThis, lType, lConfigList, lGlobalAssetList, lCalibrationList, lApplicationList, )));
  });

  if (!pIsUndo) this.mLastLibraryJson = this.toJson();
  this.mIsLoaded = true;
};

MachineAppLibraryManager.prototype.toJson = function() {
  return {
    version: this.mVersion,
    libraryList: this.mLibraryList.map(function(pType) { return pType.toJson(); })
  };
};

MachineAppLibraryManager.prototype.saveAll = function(pCallback) {
  this.getSaveFunctionality().saveLibrary(this.toJson(), function(pSuccess) {
    if (pCallback) pCallback(pSuccess);
  });
};

MachineAppLibraryManager.prototype.saveItem = function(pMachineAppType, pItemType, pJson, pCallback, pSaveOptions) {
  // TODO: This commented out behavior was used to enable redo/undo in the CAD. I am unsure as to whether or not
  //       we might want this behavior in the future.
  // const lNewJson = this.toJson(),
  // lOldJson = this.mLastLibraryJson,
  // this.mLastLibraryJson = lNewJson;

  this.getSaveFunctionality().upsert(pMachineAppType, pItemType, pJson, function(pSuccess) {
    if (pCallback) pCallback(pSuccess);
  }, pSaveOptions);
};

MachineAppLibraryManager.prototype.removeItem = function(pMachineAppType, pItemType, pJson, pCallback, pSaveOptions) {
  // TODO: This commented out behavior was used to enable redo/undo in the CAD. I am unsure as to whether or not
  //       we might want this behavior in the future.
  // const lNewJson = this.toJson(),
  // lOldJson = this.mLastLibraryJson,
  // this.mLastLibraryJson = lNewJson;

  this.getSaveFunctionality().remove(pMachineAppType, pItemType, pJson, function(pSuccess) {
    if (pCallback) pCallback(pSuccess);
  }, pSaveOptions);
};

MachineAppLibraryManager.prototype.getLastSavedLibrary = function() { return this.mLastLibraryJson; };

/**
 * Returns this list of machine app type libraries that are available to us.
 * @returns {MachineAppLibrary[]}
 */
MachineAppLibraryManager.prototype.listLibraries = function() { return this.mLibraryList; };

/**
 * Retrieves a library by type. If it does not exist, we create a new empty one.
 * @param {String} pType A MachineApp type
 * @returns {MachineAppLibrary}
 */
MachineAppLibraryManager.prototype.getLibraryByType = function(pType) {
  const lLibrary = this.listLibraries().find(function(pTypeLib) { return pTypeLib.getType() === pType; });
  if (!lLibrary) {
    return this.addLibrary(pType);
  }
  else {
    return lLibrary;
  }
};

/**
 * Add a new library to the list with the given type.
 * @param {String} pType A MachineApp type
 * @returns {MachineAppLibrary}
 */
MachineAppLibraryManager.prototype.addLibrary = function(pType) {
  const lLibrary = new MachineAppLibrary(this, pType, [], [], [], []);
  this.mLibraryList.push(lLibrary);
  this.saveAll();
  return lLibrary;
};

/**
 * Contains the configruation, calibration, and application lists that have been created
 * for this MachineApp type.
 *
 * @param {MachineAppLibraryManager} pManager Library Manager
 * @param {String} pType MachineAppType
 * @param {Object[]} pConfigurationList List of configurations associated with this app type
 * @param {Object[]} pCalibrationList List of calibrations assoicated with this app type
 * @param {Object[]} pApplicationList List of applications associated with this app type
 * @param {Object[]} pGlobalAssetList List of globalAssets associated with this app type
 */
function MachineAppLibrary(pManager, pType, pConfigurationList, pGlobalAssetList, pCalibrationList, pApplicationList) {
  const lThis = this;

  this.mManager       = pManager;
  this.mType          = pType;
  this.mVersioning   = machine_app_implementation_loader.instance.getVersioning(pType);
  this.mItemListsByType = {
    'calibration':   initializeList(pCalibrationList),
    'globalAsset':   initializeList(pGlobalAssetList),
    'configuration': initializeList(pConfigurationList),
    'application':   initializeList(pApplicationList),
  };

  function lDidVersionChange(pPrev, pCurr) {
    return pPrev.major != pCurr.major || pPrev.minor != pCurr.minor;
  }

  function getItemFromJson(pJson) {
    const lOriginalVersion = { ...pJson.version };
    const lJson = lThis.mVersioning.upgrade(pJson.version, lThis.mVersioning.getVersionList(), JSON.parse(JSON.stringify(pJson)), pJson.itemType);

    const lType     = lJson.type;
    const lUuid     = lJson.uuid;
    const lName     = lJson.name;
    const lVersion  = lJson.version;
    const lVid      = lJson.vid;
    const lItemType = lJson.itemType;

    let lItem;

    switch(lItemType) {
      case 'calibration':
        lItem = new MachineParameters(lThis, lType, lUuid, lName, lVersion, lJson.calibration, lVid);
        break;
      case 'globalAsset':
        lItem = new MachineAppGlobalAsset(lThis, lType, lJson.assetType, lUuid, lName, lVersion, lJson.globalAsset, lVid);
        break;
      case 'configuration':
        lItem = new MachineAppConfiguration(lThis, lType, lUuid, lName, lVersion, lJson.configuration, lVid);
        break;
      case 'application':
        lItem = new MachineAppApplication(lThis, lType, lUuid, lName, lVersion, lJson.appData, lJson.injectedAppJson, lVid);
        lThis.getManager().getMachineLogicLibrary().addTemporaryApplication(lItem.getInjectableApplication());
        break;
      default:
        console.error('Unknown item type: ' + lItemType);
        return;
    }

    if (lDidVersionChange(lOriginalVersion, lJson.version)) {
      lItem.setDirty();
    }
    return lItem;
  }

  function initializeList(pJsonList) {
    return pJsonList ? pJsonList.map(getItemFromJson).filter(item => item != undefined) : [];
  }
}

MachineAppLibrary.prototype.toJson = function() {
  return {
    type:               this.getType(),
    calibrationList:    this.listMachineParameters().map(function(pParameter) { return pParameter.toJson(); }),
    globalAssetList:    this.listGlobalAssets().map(function(pGlobalAsset) { return pGlobalAsset.toJson(); }),
    configurationList:  this.listConfigurations().map(function(pConfiguraton) { return pConfiguraton.toJson(); }),
    applicationList:    this.listApplications().map(function(pApplication) { return pApplication.toJson(); }),
  };
};

/**
   * @returns {MachineAppLibraryManager}
  */
MachineAppLibrary.prototype.getManager = function() { return this.mManager; };
MachineAppLibrary.prototype.getType = function() { return this.mType; };
MachineAppLibrary.prototype.getMachineAppLibrary = function() { return this.mManager; };

// GENERAL ------------------------------------------------------------------------------------
/**
 *
 * @param {string} pItemType configuration | globalAsset | calibration | application
 * @param {Object} pItem item object
 * @param {() => void} [pOnSuccess] Callback function on success
 * @param {() => void} [pOnError] Callback function on error
 * @param {Object} [pSaveOptions] save options
 * @param {bool} [pSaveOptions.saveImmediate]
 */
MachineAppLibrary.prototype.upsert = function(pItemType, pItem, pOnSuccess = () => null, pOnError = () => null, pSaveOptions = {}) {
  const lThis = this;
  this.getManager().saveItem(this.getType(), pItemType, pItem.toJson(), function(pSuccess) {
    if (!pSuccess) {
      pOnError();
      return;
    }

    const lItemList = lThis.mItemListsByType[pItemType];
    if (!lItemList) {
      console.error(`Unknown item type: ${pItemType}`);
      pOnError();
      return;
    }

    const lFoundIdx = lItemList.findIndex(pExisting => pExisting.getUuid() === pItem.getUuid());
    if (lFoundIdx < 0) {
      lItemList.push(pItem);
    }
    else {
      lItemList[lFoundIdx] = pItem;
    }

    pOnSuccess(pItem);

  }, pSaveOptions);
};

MachineAppLibrary.prototype.remove = function(pItemType, pUuid, pOnSuccess, pOnError) {
  const lItemList = this.mItemListsByType[pItemType];
  if (!lItemList) {
    console.error(`Unknown item type: ${pItemType}`);
    pOnError();
    return;
  }

  const lFoundIdx = lItemList.findIndex(pExisting => pExisting.getUuid() === pUuid);
  if (lFoundIdx < 0) {
    console.error('Could not find index for item with uuid=' + pUuid + ' and itemType=' + pItemType);
    pOnError();
    return;
  }

  this.getManager().removeItem(this.getType(), pItemType, lItemList[lFoundIdx].toJson(), function(pSuccess) {
    if(!pSuccess){
      pOnError();
      return;
    }
    lItemList.splice(lFoundIdx, 1);
    pOnSuccess();
  });
};

/**
 * @param {MachineAppConfiguration | MachineAppGlobalAsset | MachineAppCalibration | MachineAppApplication} pItem
 * @param {() => void} [pOnSuccess] Callback function on success
 * @param {() => void} [pOnError] Callback function on error
 */
MachineAppLibrary.prototype.duplicateItem = function(pItem, pOnSuccess, pOnError) {
  const lJson = pItem.toJson();
  lJson.uuid = uuidv4();
  let lOnSuccess = pOnSuccess;

  let lItem;
  switch (pItem.getItemType()) {
    case 'configuration': {
      lJson.name = this.uniqueConfigurationName(lJson.name);
      lItem = new MachineAppConfiguration(this, lJson.type, lJson.uuid, lJson.name, lJson.version, lJson.configuration);
      break;
    }
    case 'globalAsset': {
      lJson.name = this.uniqueGlobalAssetName(lJson.assetType, lJson.name);
      lItem = new MachineAppGlobalAsset(this, lJson.type, lJson.assetType, lJson.uuid, lJson.name, lJson.version, lJson.globalAsset);
      break;
    }
    case 'calibration': {
      lJson.name = this.uniqueCalibrationName(lJson.name);
      lItem = new MachineParameters(this, lJson.type, lJson.uuid, lJson.name, lJson.version, lJson.calibration);
      break;
    }
    case 'application': {
      lOnSuccess = (pItem) => {
        this.getManager().getMachineLogicLibrary().addTemporaryApplication(pItem.getInjectableApplication());
        if (pOnSuccess) pOnSuccess(pItem);
      };

      lJson.name = this.uniqueApplicationName(lJson.name);
      lItem = new MachineAppApplication(this, lJson.type, lJson.uuid, lJson.name, lJson.version, lJson.appData, lJson.injectedAppJson);
      break;
    }
    default:
      console.error('Trying to duplicate with Unknown item type', pItem);
      pOnError();
      return;
  }

  this.upsert(pItem.getItemType(), lItem, lOnSuccess, pOnError);
};

// CALIBRATION ------------------------------------------------------------------------------------
MachineAppLibrary.prototype.newCalibration = function(pName) {
  const lType   = this.mType;
  const lUuid     = uuidv4();
  const lName     = this.uniqueCalibrationName(pName);
  const lNewCalibration = new MachineParameters(this, lType, lUuid, lName, this.mVersioning.getCurrentVersion());
  return lNewCalibration;
};

MachineAppLibrary.prototype.uniqueCalibrationName = function(pName) {
  return _uniqueName(pName, this.listMachineParameters().map(function(pCalibration) { return pCalibration.getName(); }));
};

MachineAppLibrary.prototype.removeCalibration = function(pUuid, pOnSuccess, pOnError) {
  this.remove('calibration', pUuid, pOnSuccess, pOnError);
};

MachineAppLibrary.prototype.listMachineParameters = function() { return this.mItemListsByType.calibration; };

MachineAppLibrary.prototype.getMachineParameters = function() {
  if (this.listMachineParameters().length === 0) return undefined;
  return this.listMachineParameters()[0];
};

// GLOBAL ASSET ------------------------------------------------------------------------------------
MachineAppLibrary.prototype.newGlobalAsset = function(pName, pAssetType) {
  const lType = this.mType;
  const lUuid = uuidv4();
  const lAssetType = pAssetType;
  const lName = this.uniqueGlobalAssetName(pAssetType, pName);
  const lNewGlobalAsset = new MachineAppGlobalAsset(this, lType, lAssetType, lUuid, lName, this.mVersioning.getCurrentVersion());

  return lNewGlobalAsset;
};

MachineAppLibrary.prototype.uniqueGlobalAssetName = function(pAssetType, pName) {
  return _uniqueName(
    pName,
    this.listGlobalAssetsByAssetType(pAssetType)
      .map(pFilteredAsset => pFilteredAsset.getName())
  );
};

MachineAppLibrary.prototype.removeGlobalAsset = function(pUuid, pOnSuccess, pOnError) {
  this.remove('globalAsset', pUuid, pOnSuccess, pOnError);
};

MachineAppLibrary.prototype.listGlobalAssets = function() { return this.mItemListsByType.globalAsset; };

MachineAppLibrary.prototype.listGlobalAssetsByAssetType = function(pAssetType) { return this.mItemListsByType.globalAsset.filter(asset => asset.mAssetType === pAssetType); };

// CONFIGURATION ------------------------------------------------------------------------------------
MachineAppLibrary.prototype.newConfiguration = function(pName) {
  const lType   = this.mType;
  const lUuid     = uuidv4();
  const lName     = this.uniqueConfigurationName(pName);
  const lConfig   = new MachineAppConfiguration(this, lType, lUuid, lName, this.mVersioning.getCurrentVersion());

  // (mkosarek June 28, 2022) Note that configurations are not saved to the backend immediately. A user must
  // edit the configuration in anyway for the data to persist.
  return lConfig;
};

MachineAppLibrary.prototype.uniqueConfigurationName = function(pName) {
  return _uniqueName(pName, this.listConfigurations().map(function(pConfig) { return pConfig.getName(); }));
};

MachineAppLibrary.prototype.removeConfiguration = function(pUuid, pOnSuccess, pOnError) {
  this.remove('configuration', pUuid, pOnSuccess, pOnError);
};

MachineAppLibrary.prototype.listConfigurations = function() { return this.mItemListsByType.configuration; };

MachineAppLibrary.prototype.getConfiguration = function(pUuid) { return this.listConfigurations().find(function(pConfig) { return pConfig.getUuid() === pUuid; }); };

// APPLICATION ------------------------------------------------------------------------------------
MachineAppLibrary.prototype.newApplication = function(pName, pOnSuccess, pOnError) {
  const lType           = this.mType;
  const lUuid           = uuidv4();
  const lName           = this.uniqueApplicationName(pName);
  const lAppData        = {};
  const lOnSuccess      = (pItem) => {
    this.getManager().getMachineLogicLibrary().addTemporaryApplication(pItem.getInjectableApplication());
    if (pOnSuccess) pOnSuccess(pItem);
  };

  const lNewApp = new MachineAppApplication(this, lType, lUuid, lName, this.mVersioning.getCurrentVersion(), lAppData);
  this.upsert('application', lNewApp, lOnSuccess, pOnError);
};

MachineAppLibrary.prototype.uniqueApplicationName = function(pName) {
  return this.getManager().getMachineLogicLibrary().uniqueApplicationName(pName);
};

MachineAppLibrary.prototype.removeApplication = function(pUuid, pOnSuccess, pOnError) {
  const lApp = this.getApplication(pUuid);
  const lInjectable = lApp?.getInjectableApplication();
  const lOnSuccess = () => {
    this.getManager().getMachineLogicLibrary().removeTemporaryApplication(lInjectable.getName());
    if (pOnSuccess) pOnSuccess(pUuid);
  };

  this.remove('application', pUuid, lOnSuccess, pOnError);
};

MachineAppLibrary.prototype.listApplications = function() { return this.mItemListsByType.application; };

MachineAppLibrary.prototype.getApplication = function(pUuid) { return this.listApplications().find(function(pApp) { return pApp.getUuid() === pUuid; }); };

module.exports = {
  instance                        : new MachineAppLibraryManager(),
  MachineAppLibraryManager        : MachineAppLibraryManager
};
